import { useEffect, useState } from 'react';
import { passwordResetFields } from '../constants/formFields';
import Input from './Input';
import FormAction from './FormAction';
import { delay } from '../lib/helpers';
import { useAsyncFn } from '../hooks/useAsyncFn';
import {
  ErrorTypes,
  UserResetPasswordResponse,
} from '../types/auth-service-responses';

interface FormElements extends HTMLFormControlsCollection {
  password: HTMLInputElement;
  confirmPassword: HTMLInputElement;
  resetHash: HTMLInputElement;
}
interface SignupPasswordFormElement extends HTMLFormElement {
  readonly elements: FormElements;
}

const fields = passwordResetFields;
const fieldsState: { [key: string]: string } = {};
fields.forEach((field) => (fieldsState[field.id] = ''));

export default function ResetPassword() {
  const [resetState, setResetState] = useState(fieldsState);
  const [formErrorState, setFormErrorState] = useState<
    ErrorTypes | undefined
  >();
  const [formSuccessState, setFormSuccessState] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | undefined>();

  useEffect(() => {
    const url = new URL(window.location.href);

    if (url.searchParams.get('hash')) {
      // we got an email, update the field
      setResetState({
        ...resetState,
        resetHash: url.searchParams.get('hash') || '',
      });
    }
    if (url.searchParams.get('redirectUrl')) {
      setRedirectUrl(url.searchParams.get('redirectUrl') || undefined);
    }
  }, [resetState]);

  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const btn = e.target as HTMLInputElement;
    setResetState({ ...resetState, [btn.id]: btn.value });
  };

  const getResetPasswordUrl = (): string => {
    return `/reset/password`;
  };
  const getLoginUrl = (): string => {
    // return `/login`;
    // TODO this is a hack for now, we need to fix
    return redirectUrl ? redirectUrl : 'https://app.bloodygoodtests.com.au';
  };

  const [{ loading: isSubmitting, value: submitResponse }, handleSubmit] =
    useAsyncFn(async (event: React.FormEvent<SignupPasswordFormElement>) => {
      event.preventDefault();

      if (
        event.currentTarget.elements.password.value !==
        event.currentTarget.elements.confirmPassword.value
      ) {
        setFormErrorState(ErrorTypes.PasswordsDoNotMatch);
        return;
      }
      const response = await fetch('/api/users/reset/password/complete', {
        method: 'POST',
        headers: {
          'content-type': 'application/x-www-form-urlencoded;charset=UTF-8',
          accept: 'application/json',
        },
        body: new URLSearchParams({
          password: event.currentTarget.elements.password.value,
          confirmPassword: event.currentTarget.elements.confirmPassword.value,
          resetHash: event.currentTarget.elements.resetHash.value,
        }),
      });

      try {
        const payload = (await response.json()) as UserResetPasswordResponse;

        if (payload.error && payload.type !== undefined) {
          setFormErrorState(payload.type);
          return payload;
        }

        setFormErrorState(undefined);
        setFormSuccessState(true);

        await delay(5000);

        // TODO this is a hack for now, we need to fix
        window.location.href = redirectUrl
          ? redirectUrl
          : 'https://app.bloodygoodtests.com.au';
        // navigate('/login');
      } catch (e) {
        setFormErrorState(ErrorTypes.UnknownError);
      }
      return;
    }, []);

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      {formErrorState && (
        <div
          className="bg-eirly-100/10 border-t-4 border-eirly-500 rounded-b text-teal-900 px-4 py-3 shadow-md"
          role="alert"
        >
          <div className="flex">
            <div className="py-1">
              <svg
                className="fill-current h-6 w-6 text-eirly-500 mr-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z" />
              </svg>
            </div>
            <div>
              <p className="font-bold">Ooops...</p>

              {formErrorState === ErrorTypes.UnknownError && (
                <p className="text-sm">
                  Server returned an error, please try again later
                </p>
              )}
              {formErrorState === ErrorTypes.PasswordsDoNotMatch && (
                <p className="text-sm">The passwords do not match</p>
              )}
              {formErrorState === ErrorTypes.MissingPasswordResetHash && (
                <p className="text-sm">
                  Unable to process the request as the reset hash was missing
                </p>
              )}
              {formErrorState === ErrorTypes.PasswordTooWeak &&
                submitResponse && (
                  <p className="text-sm whitespace-pre-line">
                    {submitResponse?.message}
                  </p>
                )}
              {formErrorState === ErrorTypes.HashInvalidOrExpired && (
                <p className="text-sm">
                  The reset hash is invalid or expired, try{' '}
                  <a
                    className="text-eirly-600 hover:text-eirly-500"
                    href={getResetPasswordUrl()}
                  >
                    resetting your password
                  </a>{' '}
                  again.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      {formSuccessState && (
        <div
          className="bg-eirly-alt-100/10 border-t-4 border-eirly-alt-500 rounded-b text-teal-900 px-4 py-3 shadow-md"
          role="alert"
        >
          <div className="flex">
            <div className="py-1">
              <svg
                className="fill-current h-6 w-6 text-eirly-alt-500 mr-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z" />
              </svg>
            </div>
            <div>
              <p className="font-bold">Success</p>
              <p className="text-sm">
                Password reset complete, navigating you back to the{' '}
                <a
                  className="text-eirly-600 hover:text-eirly-500"
                  href={getLoginUrl()}
                >
                  login
                </a>{' '}
                page
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="-space-y-px">
        {fields.map((field) => (
          <Input
            key={field.id}
            handleChange={handleChange}
            value={resetState[field.id]}
            labelText={field.labelText}
            labelFor={field.labelFor}
            id={field.id}
            name={field.name}
            type={field.type}
            isRequired={field.isRequired}
            note={field.note}
            placeholder={field.placeholder}
          />
        ))}
      </div>

      <div className="h-4" />

      <FormAction disabled={isSubmitting} text="Reset Password" />
    </form>
  );
}
